import { useState } from 'react';
import { api } from '@/services/api';
import { downloadTextFile } from '@/shared/exports';
import type { Vehicle, Compartment } from './types';

/**
 * Modal "Esporta TXT": 2 step. Step 1 = selezione mezzi, step 2 = opzioni
 * (solo matricola sì/no). Fetch albero compartments per ogni mezzo, render
 * indentato come TXT plain-text.
 */
export function VehicleExportModal({ vehicles, onClose }: { vehicles: Vehicle[]; onClose: () => void }): React.JSX.Element {
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [onlyMatricola, setOnlyMatricola] = useState(false);
  const [step, setStep] = useState<1 | 2>(1);

  async function doExport(): Promise<void> {
    const lines: string[] = [];
    lines.push(`EXPORT MEZZI - ${new Date().toLocaleString('it-IT')}`);
    lines.push('='.repeat(60));
    for (const v of vehicles.filter(v => selected.has(v.id))) {
      lines.push('');
      lines.push(`### ${v.name}${v.plate ? ` (${v.plate})` : ''} ###`);
      const res = await api.get(`/vehicles/${v.id}/compartments`);
      const comps: Compartment[] = res.data;
      const tree = comps.filter(c => !c.parentCompartmentId);
      printCompartments(tree, comps, 0, lines, onlyMatricola);
    }
    downloadTextFile(lines.join('\n'), `mezzi_${new Date().toISOString().slice(0, 10)}.txt`);
    onClose();
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 480 }}>
        {step === 1 && (
          <>
            <h3>📄 Export TXT — Step 1/2: seleziona mezzi</h3>
            {vehicles.map(v => (
              <label key={v.id} className="checkbox-group" style={{ padding: 8 }}>
                <input type="checkbox" checked={selected.has(v.id)}
                  onChange={e => {
                    const s = new Set(selected);
                    if (e.target.checked) s.add(v.id); else s.delete(v.id);
                    setSelected(s);
                  }} />
                <span style={{ marginLeft: 8 }}>{v.name} {v.plate && <small style={{ opacity: 0.6 }}>({v.plate})</small>}</span>
              </label>
            ))}
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 20 }}>
              <button onClick={onClose} className="btn-secondary" style={{ padding: '10px 20px' }}>Annulla</button>
              <button onClick={() => setStep(2)} disabled={selected.size === 0}
                style={{ padding: '10px 22px', fontWeight: 700, border: 'none', borderRadius: 8,
                         cursor: selected.size === 0 ? 'not-allowed' : 'pointer',
                         background: 'linear-gradient(135deg, #28a745 0%, #218838 100%)', color: 'white' }}>
                Avanti →
              </button>
            </div>
          </>
        )}
        {step === 2 && (
          <>
            <h3>📄 Export TXT — Step 2/2: opzioni</h3>
            <label className="checkbox-group" style={{ padding: 8 }}>
              <input type="checkbox" checked={onlyMatricola} onChange={e => setOnlyMatricola(e.target.checked)} />
              <span style={{ marginLeft: 8 }}>Solo oggetti con matricola</span>
            </label>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 20 }}>
              <button onClick={() => setStep(1)} className="btn-secondary" style={{ padding: '10px 20px' }}>← Indietro</button>
              <button onClick={doExport}
                style={{ padding: '10px 22px', fontWeight: 700, border: 'none', borderRadius: 8, cursor: 'pointer',
                         background: 'linear-gradient(135deg, #28a745 0%, #218838 100%)', color: 'white' }}>
                📄 Scarica TXT
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function printCompartments(tree: Compartment[], all: Compartment[], depth: number, lines: string[], onlyMatricola: boolean): void {
  for (const c of tree) {
    lines.push(`${'  '.repeat(depth)}[${c.name}]`);
    const items = onlyMatricola ? (c.items ?? []).filter(i => i.serialNumber) : (c.items ?? []);
    for (const i of items) {
      const sn = i.serialNumber ? ` #${i.serialNumber}` : '';
      lines.push(`${'  '.repeat(depth + 1)}- ${i.name} (${i.quantity} ${i.unit ?? ''})${sn}`);
    }
    const children = all.filter(cc => cc.parentCompartmentId === c.id);
    printCompartments(children, all, depth + 1, lines, onlyMatricola);
  }
}
